// src/store/slices/cashDayBookSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { cashDayBookApi } from '../../api'

// Async thunks for API calls
export const createEntry = createAsyncThunk(
  'cashDayBook/createEntry',
  async (entryData, { rejectWithValue }) => {
    try {
      const response = await cashDayBookApi.createEntry(entryData)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create entry')
    }
  }
)

export const fetchUserEntries = createAsyncThunk(
  'cashDayBook/fetchUserEntries',
  async (params, { rejectWithValue }) => {
    try {
      const response = await cashDayBookApi.getUserEntries(params)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch entries')
    }
  }
)

export const fetchAllEntries = createAsyncThunk(
  'cashDayBook/fetchAllEntries',
  async (params, { rejectWithValue }) => {
    try {
      const response = await cashDayBookApi.getAllEntries(params)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch all entries')
    }
  }
)

export const deleteEntry = createAsyncThunk(
  'cashDayBook/deleteEntry',
  async (entryId, { rejectWithValue }) => {
    try {
      await cashDayBookApi.deleteEntry(entryId)
      return entryId
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to delete entry')
    }
  }
)

export const fetchUserCategories = createAsyncThunk(
  'cashDayBook/fetchUserCategories',
  async (userId, { rejectWithValue }) => {
    try {
      const response = await cashDayBookApi.getUserCategories(userId)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch categories')
    }
  }
)

export const fetchSummary = createAsyncThunk(
  'cashDayBook/fetchSummary',
  async (params, { rejectWithValue }) => {
    try {
      const response = await cashDayBookApi.getSummary(params)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch summary')
    }
  }
)

export const closeCashBook = createAsyncThunk(
  'cashDayBook/closeCashBook',
  async ({ userId, endDate }, { rejectWithValue }) => {
    try {
      const response = await cashDayBookApi.closeCashBook(userId, endDate)
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to close cash book')
    }
  }
)

export const fetchEmployees = createAsyncThunk(
  'cashDayBook/fetchEmployees',
  async (_, { rejectWithValue }) => {
    try {
      const response = await cashDayBookApi.getEmployees()
      return response.data?.data
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch employees')
    }
  }
)

const initialState = {
  entries: [],
  categories: [],
  employees: [],
  summary: {
    totalIncome: 0,
    totalExpense: 0,
    balance: 0,
    incomeCount: 0,
    expenseCount: 0
  },
  categoryBreakdown: [],
  activeCategory: '',
  activeType: 'Income',
  pagination: {
    current: 1,
    total: 1,
    count: 0
  },
  filters: {
    type: '',
    category: '',
    startDate: '',
    endDate: '',
    designation: '',
    userId: ''
  },
  loading: {
    entries: false,
    creating: false,
    deleting: false,
    categories: false,
    summary: false,
    employees: false,
    closing: false
  },
  error: null,
  successMessage: null
}

const cashDayBookSlice = createSlice({
  name: 'cashDayBook',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null
    },
    clearSuccessMessage: state => {
      state.successMessage = null
    },
    setActiveCategory: (state, action) => {
      state.activeCategory = action.payload
    },
    setActiveType: (state, action) => {
      state.activeType = action.payload
    },
    resetActiveCategory: state => {
      state.activeCategory = ''
      state.activeType = 'Income'
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: state => {
      state.filters = initialState.filters
    },
    updateEntry: (state, action) => {
      const index = state.entries.findIndex(
        entry => entry._id === action.payload._id
      )
      if (index !== -1) {
        state.entries[index] = action.payload
      }
    }
  },
  extraReducers: builder => {
    builder
      // Create Entry
      .addCase(createEntry.pending, state => {
        state.loading.creating = true
        state.error = null
      })
      .addCase(createEntry.fulfilled, (state, action) => {
        state.loading.creating = false
        state.entries.unshift(action.payload)
        state.successMessage = 'Entry created successfully'
      })
      .addCase(createEntry.rejected, (state, action) => {
        state.loading.creating = false
        state.error = action.payload
      })

      // Fetch User Entries
      .addCase(fetchUserEntries.pending, state => {
        state.loading.entries = true
        state.error = null
      })
      .addCase(fetchUserEntries.fulfilled, (state, action) => {
        state.loading.entries = false
        state.entries = action.payload.entries
        state.pagination = action.payload.pagination
        state.summary.balance = action.payload.balance
      })
      .addCase(fetchUserEntries.rejected, (state, action) => {
        state.loading.entries = false
        state.error = action.payload
      })

      // Fetch All Entries
      .addCase(fetchAllEntries.pending, state => {
        state.loading.entries = true
        state.error = null
      })
      .addCase(fetchAllEntries.fulfilled, (state, action) => {
        state.loading.entries = false
        state.entries = action.payload.entries
        state.pagination = action.payload.pagination
      })
      .addCase(fetchAllEntries.rejected, (state, action) => {
        state.loading.entries = false
        state.error = action.payload
      })

      // Delete Entry
      .addCase(deleteEntry.pending, state => {
        state.loading.deleting = true
        state.error = null
      })
      .addCase(deleteEntry.fulfilled, (state, action) => {
        state.loading.deleting = false
        state.entries = state.entries.filter(
          entry => entry._id !== action.payload
        )
        state.successMessage = 'Entry deleted successfully'
      })
      .addCase(deleteEntry.rejected, (state, action) => {
        state.loading.deleting = false
        state.error = action.payload
      })

      // Fetch Categories
      .addCase(fetchUserCategories.pending, state => {
        state.loading.categories = true
        state.error = null
      })
      .addCase(fetchUserCategories.fulfilled, (state, action) => {
        state.loading.categories = false
        state.categories = action.payload
      })
      .addCase(fetchUserCategories.rejected, (state, action) => {
        state.loading.categories = false
        state.error = action.payload
      })

      // Fetch Summary
      .addCase(fetchSummary.pending, state => {
        state.loading.summary = true
        state.error = null
      })
      .addCase(fetchSummary.fulfilled, (state, action) => {
        state.loading.summary = false
        state.summary = action.payload.summary
        state.categoryBreakdown = action.payload.categoryBreakdown
      })
      .addCase(fetchSummary.rejected, (state, action) => {
        state.loading.summary = false
        state.error = action.payload
      })

      // Close Cash Book
      .addCase(closeCashBook.pending, state => {
        state.loading.closing = true
        state.error = null
      })
      .addCase(closeCashBook.fulfilled, (state, action) => {
        state.loading.closing = false
        state.successMessage = `Cash book closed successfully. ${action.payload.closedEntries} entries finalized.`
      })
      .addCase(closeCashBook.rejected, (state, action) => {
        state.loading.closing = false
        state.error = action.payload
      })

      // Fetch Employees
      .addCase(fetchEmployees.pending, state => {
        state.loading.employees = true
        state.error = null
      })
      .addCase(fetchEmployees.fulfilled, (state, action) => {
        state.loading.employees = false
        state.employees = action.payload
      })
      .addCase(fetchEmployees.rejected, (state, action) => {
        state.loading.employees = false
        state.error = action.payload
      })
  }
})

export const {
  clearError,
  clearSuccessMessage,
  setActiveCategory,
  setActiveType,
  resetActiveCategory,
  setFilters,
  clearFilters,
  updateEntry
} = cashDayBookSlice.actions

// Selectors
export const selectCashDayBook = state => state.cashDayBook
export const selectEntries = state => state.cashDayBook.entries
export const selectCategories = state => state.cashDayBook.categories
export const selectEmployees = state => state.cashDayBook.employees
export const selectSummary = state => state.cashDayBook.summary
export const selectCategoryBreakdown = state =>
  state.cashDayBook.categoryBreakdown
export const selectPagination = state => state.cashDayBook.pagination
export const selectFilters = state => state.cashDayBook.filters
export const selectLoading = state => state.cashDayBook.loading
export const selectError = state => state.cashDayBook.error
export const selectSuccessMessage = state => state.cashDayBook.successMessage

export default cashDayBookSlice.reducer
